import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import InputBase from "@mui/material/InputBase";
import { gr } from "../tokens";

// Number field with − / + (measurements: reps, weight, %). Typed or stepped; never below `min`.
// value / onChange use strings like a text field ("" = empty). `decimals` allows 82.5.
export const grMeasureBoxSx = (error) => ({
  display: "flex",
  alignItems: "stretch",
  // Compact (32px): the check-in form lists several of these.
  minHeight: gr.control.sm,
  border: `1px solid ${error ? gr.color.danger : gr.color.line}`,
  borderRadius: gr.radius.control,
  background: gr.color.surface,
  overflow: "hidden",
  transition: "border-color .15s ease, box-shadow .15s ease",
  "&:hover": { borderColor: error ? gr.color.danger : gr.color.lineStrong },
  "&:focus-within": { borderColor: gr.color.brand, boxShadow: gr.shadow.focus },
});

// Right-aligned number inside a measurement box (GrNumberStepper, GrDurationField).
export const grMeasureInputSx = {
  flex: 1,
  minWidth: 0,
  px: gr.space[2],
  fontFamily: gr.font.family,
  color: gr.color.ink,
  "& input": {
    p: 0,
    textAlign: "right",
    fontSize: gr.font.size.md,
    fontWeight: gr.font.weight.semibold,
    fontVariantNumeric: "tabular-nums",
    "&::placeholder": { color: gr.color.lineStrong, opacity: 1 },
  },
};

export const grMeasureUnitSx = {
  alignSelf: "center",
  pr: gr.space[2],
  fontSize: gr.font.size.sm,
  color: gr.color.muted,
};

const stepSx = {
  flex: "none",
  width: gr.control.sm,
  background: gr.color.bg,
  color: gr.color.brandDark,
  fontFamily: gr.font.family,
  fontSize: gr.font.size.md,
  fontWeight: gr.font.weight.semibold,
  "&:hover": { background: gr.color.brandSoft },
  "&.Mui-focusVisible": { background: gr.color.brandSoft },
  "&.Mui-disabled": { color: gr.color.lineStrong },
};

const toNumber = (v) => {
  if (v == null || String(v).trim() === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

export default function GrNumberStepper({
  value,
  onChange,
  unit,
  step = 1,
  min = 0,
  decimals = false,
  error = false,
  id,
  ariaLabel,
  placeholder = "0",
  sx,
}) {
  const n = toNumber(value);
  const bump = (delta) => {
    const next = Math.max(min, Math.round(((n ?? 0) + delta) * 100) / 100);
    onChange(String(next));
  };
  return (
    <Box sx={{ ...grMeasureBoxSx(error), ...sx }}>
      <ButtonBase
        disableRipple
        aria-label="Decrease"
        disabled={n == null || n <= min}
        onClick={() => bump(-step)}
        sx={{ ...stepSx, borderRight: `1px solid ${gr.color.line}` }}
      >
        −
      </ButtonBase>
      <InputBase
        id={id}
        value={value == null ? "" : String(value)}
        placeholder={placeholder}
        inputProps={{
          inputMode: decimals ? "decimal" : "numeric",
          "aria-label": ariaLabel,
          "aria-invalid": error || undefined,
        }}
        onChange={(e) => {
          const raw = String(e.target.value);
          const text = decimals
            ? raw.replace(/[^\d.,]/g, "").slice(0, 10)
            : raw.replace(/\D/g, "").slice(0, 6);
          onChange(text);
        }}
        sx={grMeasureInputSx}
      />
      {unit ? (
        <Box component="span" sx={grMeasureUnitSx}>
          {unit}
        </Box>
      ) : null}
      <ButtonBase
        disableRipple
        aria-label="Increase"
        onClick={() => bump(step)}
        sx={{ ...stepSx, borderLeft: `1px solid ${gr.color.line}` }}
      >
        +
      </ButtonBase>
    </Box>
  );
}

GrNumberStepper.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  unit: PropTypes.node,
  step: PropTypes.number,
  min: PropTypes.number,
  decimals: PropTypes.bool,
  error: PropTypes.bool,
  id: PropTypes.string,
  ariaLabel: PropTypes.string,
  placeholder: PropTypes.string,
  sx: PropTypes.object,
};
